import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './../api/axios';
import { useNavigate } from 'react-router-dom';

const EVENT_OPTIONS = [
  { key: 'newMessages', label: 'New inbound reply', description: 'A lead replies to an active conversation.' },
  { key: 'newConnections', label: 'Connection accepted', description: 'A LinkedIn connection becomes available for messaging.' },
  { key: 'leadQualified', label: 'Lead qualified', description: 'Agent or workflow qualification reaches the configured threshold.' },
  { key: 'meetingBooked', label: 'Meeting booked', description: 'A tracked meeting is successfully booked.' },
  { key: 'meetingLifecycle', label: 'Meeting lifecycle updates', description: 'Reschedules, cancellations, completions and no-shows for tracked meetings.' },
  { key: 'campaignCompleted', label: 'Campaign outcome reached', description: 'The campaign reaches its configured business outcome.' },
  { key: 'agentActionProposed', label: 'Agent action proposed', description: 'Agent Mode creates an action that requires approval.' },
  { key: 'agentActionApproved', label: 'Agent action approved', description: 'An agent proposal is approved for execution.' },
  { key: 'agentActionCompleted', label: 'Agent action completed', description: 'An approved agent action is executed successfully.' },
  { key: 'agentActionFailed', label: 'Agent action failed', description: 'An agent execution needs attention.' },
];

const SlackIntegration = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState('');
  const [preferences, setPreferences] = useState({});
  const [testMessage, setTestMessage] = useState('Leadgenflow test notification');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  const SLACK_CLIENT_ID = process.env.REACT_APP_SLACK_CLIENT_ID;
  const SLACK_REDIRECT_URI = `${window.location.origin}/settings`;
  const SLACK_OAUTH_URL = `https://slack.com/oauth/v2/authorize?client_id=${SLACK_CLIENT_ID}&scope=channels:read,groups:read,chat:write,users:read&redirect_uri=${encodeURIComponent(SLACK_REDIRECT_URI)}&state=slack`;

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/integrations/slack/status');
      setStatus(data);
      setChannelId(data.channelId || '');
      setPreferences(data.logPreferences || {});
      if (data.connected) {
        const channelsResponse = await api.get('/integrations/slack/channels');
        setChannels(channelsResponse.data || []);
      } else setChannels([]);
    } catch (error) {
      console.error('Slack status error:', error);
      setFeedback(error.response?.data?.message || 'Could not load Slack status.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const isSlackRedirect = params.get('state') === 'slack';
    const initialize = async () => {
      if (code && isSlackRedirect) {
        setBusy(true);
        try {
          await api.post('/integrations/slack/slack-auth', { code, redirect_uri: SLACK_REDIRECT_URI });
          setFeedback('Slack connected successfully. Choose a channel and the events you want to receive.');
          navigate('/settings?tab=integrations&integration=slack', { replace: true });
        } catch (error) {
          setFeedback(error.response?.data?.message || 'Slack authorization failed.');
        } finally { setBusy(false); }
      }
      await load();
    };
    initialize();
  }, [load, navigate, SLACK_REDIRECT_URI]);

  const selectedChannel = useMemo(() => channels.find((channel) => channel.id === channelId), [channels, channelId]);

  const save = async () => {
    setBusy(true); setFeedback('');
    try {
      const { data } = await api.put('/integrations/slack/user/slack-integration', {
        channelId,
        channelName: selectedChannel?.name || status?.channelName || '',
        logPreferences: preferences,
      });
      setStatus(data.slackIntegration || status);
      setFeedback('Slack notification settings saved.');
    } catch (error) { setFeedback(error.response?.data?.message || 'Could not save Slack settings.'); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!channelId) return setFeedback('Choose a Slack channel first.');
    setBusy(true); setFeedback('');
    try {
      await api.post('/integrations/slack/sendMessage', { channelId, text: testMessage || 'Leadgenflow test notification' });
      setFeedback('Test notification sent successfully.');
    } catch (error) {
      const message = error.response?.data?.message || 'Could not send the test notification.';
      setFeedback(message === 'not_in_channel' ? 'Invite the Leadgenflow app to this channel first.' : message);
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete('/integrations/slack/connection');
      setStatus({ connected: false }); setChannels([]); setChannelId(''); setPreferences({});
      setFeedback('Slack disconnected.');
    } catch (error) { setFeedback(error.response?.data?.message || 'Could not disconnect Slack.'); }
    finally { setBusy(false); }
  };

  if (!status?.connected) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div><h4 className="font-semibold text-gray-950">Connect your Slack workspace</h4><p className="mt-1 text-sm text-gray-600">Leadgenflow stores the Slack token server-side. The browser never receives or persists the workspace access token.</p></div>
          <button disabled={busy || !SLACK_CLIENT_ID} onClick={() => { window.location.href = SLACK_OAUTH_URL; }} className="shrink-0 rounded-lg bg-[#4A154B] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"><i className="fab fa-slack mr-2" />Connect Slack</button>
        </div>
        {feedback && <div className="mt-4 rounded-lg bg-white p-3 text-sm text-gray-700 ring-1 ring-gray-200">{feedback}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {feedback && <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">{feedback}</div>}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-gray-200 p-4">
          <label className="text-sm font-semibold text-gray-900">Notification channel</label>
          <p className="mt-1 text-xs text-gray-500">Choose where Leadgenflow sends operational and sales events.</p>
          <select value={channelId} onChange={(event) => setChannelId(event.target.value)} className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm">
            <option value="">Select a channel…</option>
            {channels.map((channel) => <option key={channel.id} value={channel.id}>#{channel.name}{channel.isMember ? '' : ' · app not joined'}</option>)}
          </select>
          {status?.team?.name && <div className="mt-2 text-xs text-gray-500">Workspace: {status.team.name}</div>}
        </div>
        <div className="rounded-xl border border-gray-200 p-4">
          <label className="text-sm font-semibold text-gray-900">Send test</label>
          <p className="mt-1 text-xs text-gray-500">Verify permissions and channel membership before enabling events.</p>
          <div className="mt-3 flex gap-2"><input value={testMessage} onChange={(event) => setTestMessage(event.target.value)} className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" /><button disabled={busy || !channelId} onClick={sendTest} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">Send</button></div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3"><h4 className="text-sm font-semibold text-gray-900">Events sent to Slack</h4><p className="mt-0.5 text-xs text-gray-500">Only enable events your team needs. Agent events come from the audited Agent execution lifecycle.</p></div>
        <div className="divide-y divide-gray-100">
          {EVENT_OPTIONS.map((item) => <label key={item.key} className="flex cursor-pointer items-start justify-between gap-4 px-4 py-3 hover:bg-gray-50"><div><div className="text-sm font-medium text-gray-900">{item.label}</div><div className="mt-0.5 text-xs text-gray-500">{item.description}</div></div><input type="checkbox" checked={preferences[item.key] === true} onChange={(event) => setPreferences((current) => ({ ...current, [item.key]: event.target.checked }))} className="mt-1 h-4 w-4 rounded border-gray-300" /></label>)}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3"><button disabled={busy || !channelId} onClick={save} className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{busy ? 'Saving…' : 'Save Slack settings'}</button><button disabled={busy} onClick={disconnect} className="text-sm font-medium text-red-600 hover:text-red-700">Disconnect Slack</button></div>
    </div>
  );
};

export default SlackIntegration;
